import { lazy } from "react";
import { Navigate } from "react-router-dom";

/****Layouts*****/
const FullLayout = lazy(() => import("component/layout/FullLayout.js"));

/***** Pages ****/
const SignIn = lazy(() => import("component/SignIn.js"));
const InitialSetup = lazy(() => import("component/InitialSetup.js"));
const InviteAccept = lazy(() => import("component/InviteAccept.js"));
const LicenseIssuance = lazy(() => import("component/LicenseIssuance.js"));
const AdminLicenseManagement = lazy(() => import("component/AdminLicenseManagement.js"));
const AccountManagement = lazy(() => import("component/AccountManagement.js"));
const ContractDetail = lazy(() => import("component/ContractDetail.js"));
const LicenseDetail = lazy(() => import("component/LicenseDetail.js"));
const ClientRegister = lazy(() => import("component/ClientRegister.js"));
const LicenseValidation = lazy(() => import("component/LicenseValidation.js"));
const DownloadHistory = lazy(() => import("component/DownloadHistory.js"));
const AuthLogList = lazy(() => import("component/AuthLogList.js"));
const AuthLogDetail = lazy(() => import("component/AuthLogDetail.js"));
const Settings = lazy(() => import("component/Settings.js"));
const AdminRoute = lazy(() => import("component/common/AdminRoute.js"));
const RequireAuth = lazy(() => import("component/common/RequireAuth.js"));
const GuestOnly = lazy(() => import("component/common/GuestOnly.js"));

/*****Routes******/
/**
 * 아직 이관하지 않은 것: 대시보드(Partner / Non-Partner 현황). 백엔드가 3단계에서 제거돼
 * 부를 API 가 없다 — 이관이 아니라 재구축이다. TFL 마스터 라이선스는 폐기 대상이라 옮기지 않는다.
 *
 * 설정(메일)은 재구축했다. 인증 서버 URL 탭은 되살리지 않았다 — 그 값은 발급 서버의
 * TFL_AUTH_SERVER_URL 이 발급 시점에 add_flag 로 실어 보낸다. 화면에도 두면 주인이 둘이 된다.
 *
 * 파일 검증은 옮겼다 — 발급 서버에 검증 API 를 노출하고 포탈이 파일을 넘긴다.
 * 다운로드 이력도 옮겼다 — 이력은 발급 서버가 쓰고 포탈은 원장 쪽 표를 읽는다.
 *
 * 새로 만든 것: 발급 한도 관리. 한도가 없으면 발급이 거부되는데 넣을 화면이 없었다.
 */

/**
 * 본사 전용 화면.
 *
 * <b>라우트 가드와 메뉴 노출이 이 하나를 본다.</b> 둘을 따로 적으면 메뉴만 감추고 주소는
 * 열려 있는 상태가 조용히 생긴다 - 실제로 계약 화면이 그랬다. 파트너가 주소를 직접 쳐서
 * 들어갔고, 메뉴에 없으니 아무도 몰랐다.
 */
// "/settings" 는 없다. 같은 경로에 두 화면이 있고(본사는 발송 현황, 파트너는 수신 주소),
// 파트너의 것은 자기 파트너사의 것만 다룬다.
const adminOnlyPaths = new Set(["/licenses/manage", "/accounts",
                                "/contract/:contractId",
                                "/validation", "/downloads",
                                "/auth-logs", "/auth-logs/:licenseId"]);

// 주소를 직접 친 파트너를 자기 화면으로 돌려보낸다. 안내일 뿐이고, 막는 것은 서버다.
const guarded = (routes) => routes.map((route) => (
    adminOnlyPaths.has(route.path)
        ? { ...route, element: <AdminRoute>{route.element}</AdminRoute> }
        : route
));

const main = [
    {
        path: "/",
        // 로그인 여부를 <b>그릴 때</b> 본다. 여기서 직접 localStorage 를 읽으면 이 배열이
        // 만들어지는 순간의 값으로 굳는다 - 그 문서가 사는 동안 판단이 바뀌지 않는다.
        element: <RequireAuth><FullLayout/></RequireAuth>,
        children: guarded([
            { path: "/", element: <Navigate to="/licenses" replace/> },

            // 메뉴 미노출 — 목록에서 진입한다.
            { path: "/license/:licenseId", exact: true, element: <LicenseDetail/> },

            // ────────────── 메뉴 노출 순서 (Sidebar 와 동일) ──────────────
            // 발급 내역. 보이는 것이 권한에 따라 다르다 - 본사는 파트너들이 발급한 것과
            // 타입별 집계를, 파트너는 자기가 발급한 것을 본다. 발급 버튼도 이 화면에 있다.
            { path: "/licenses", exact: true, element: <LicenseIssuance/>, titleKey: "nav.licenseList" },
            // 본사가 개별 라이선스에 닿는 유일한 자리. /licenses 는 본사에게 계약·파트너
            // 단위 집계(Partner 현황)를 보여 주므로 라이선스 한 건을 다룰 수 없다.
            { path: "/licenses/manage", exact: true, element: <AdminLicenseManagement/>, titleKey: "nav.licenseManagement" },
            // 메뉴 없이 주소로만 열리던 발급 페이지가 있던 자리. 발급은 발급 내역 화면의 모달로만
            // 한다. 지우기만 하면 아래 /license/:licenseId 가 "register" 를 라이선스 번호로 받는다.
            { path: "/license/register", exact: true, element: <Navigate to="/licenses" replace/> },
            { path: "/client/register", exact: true, element: <ClientRegister/>, titleKey: "nav.clientRegister" },
            // 파트너가 왜 못 들어오는지 답하는 자리. 계정 상태와 계약 유예를 나란히 둔다.
            { path: "/accounts", exact: true, element: <AccountManagement/>, titleKey: "nav.accounts" },
            // 메뉴 미노출 — 계약 목록에서 진입한다. 라이선스 상세와 같은 관계다.
            { path: "/contract/:contractId", exact: true, element: <ContractDetail/>, titleKey: "nav.contractDetail" },
            { path: "/validation", exact: true, element: <LicenseValidation/>, titleKey: "nav.validation" },
            { path: "/downloads", exact: true, element: <DownloadHistory/>, titleKey: "nav.downloadHistory" },
            // 인증 이력. 인증 서버가 쓴 표를 읽는다 - 포탈은 만들지도 지우지도 않는다.
            { path: "/auth-logs", exact: true, element: <AuthLogList/>, titleKey: "nav.authLog" },
            // 메뉴 미노출 - 집계 목록에서 진입한다. 계약 상세와 같은 관계다.
            { path: "/auth-logs/:licenseId", exact: true, element: <AuthLogDetail/>, titleKey: "nav.authLogDetail" },

            // 사이드바 하단에 따로 렌더링된다(Sidebar.js). 아래 navigation 배열에는 넣지 않는다.
            { path: "/settings", exact: true, element: <Settings/>, titleKey: "nav.settings" },
        ]),
    },
    {
        path: "/login",
        // 세션이 있으면 들여보내지 않는다. 뒤로가기로 이 주소에 닿는 길이 있다.
        element: <GuestOnly><SignIn/></GuestOnly>
    },
    // 계정이 하나도 없을 때만 진입한다.
    {
        path: "/setup",
        element: <InitialSetup/>
    },
    // 초대 수락. 로그인 없이 열린다 - 부르는 쪽에는 아직 계정이 없고,
    // 소유 증명은 주소에 실린 토큰이 한다.
    {
        path: "/invite/:token",
        element: <InviteAccept/>
    },
    // 이관 전 TFL 에서 발급 내역이 있던 주소. 손에 익은 주소와 북마크를 흡수한다 -
    // 라우트가 없으면 흰 화면이 되고, 콘솔에도 아무것도 남지 않아 원인을 찾을 수 없다.
    {
        path: "/dashboard",
        element: <Navigate to="/licenses" replace/>
    },
    // 어느 것과도 맞지 않는 주소. **이것이 없으면 useRoutes 가 null 을 돌려주고 화면이
    // 통째로 빈다** - 오류도 404 도 없이 흰 화면이라, 오타 하나가 조용한 고장이 된다.
    {
        path: "*",
        element: <Navigate to="/licenses" replace/>
    },
]

/**
 * 사이드바 네비게이션.
 *
 * 라우트 배열(main)과 별도로 관리한다 — 라우트는 있지만 메뉴에 없는 화면(상세)이 있다.
 * 다만 본사 전용 여부는 적지 않는다 — adminOnlyPaths 에서 끌어온다.
 */
// 라이선스 발급은 메뉴가 아니다. 발급 내역 화면의 버튼으로 연다 - 발급하려면 어차피
// 그 목록을 보고 있어야 한다.
export const navigation = [
    // 한 화면이 역할에 따라 다른 것을 답하므로 메뉴 이름도 갈린다 - 본사는 파트너 현황을,
    // 파트너는 자기 발급 내역을 본다.
    { path: "/licenses",         titleKey: "nav.licenseList", adminTitleKey: "nav.partnerStatus" },
    // 발급된 라이선스의 상태를 다루는 곳. 읽는 화면이 아니라 조작하는 화면이다.
    { path: "/licenses/manage",  titleKey: "nav.licenseManagement" },
    { path: "/client/register",  titleKey: "nav.clientRegister" },
    { path: "/accounts",         titleKey: "nav.accounts" },
    // 계약은 본사가 만든다. 파트너에게는 메뉴도 화면도 열지 않는다. 다만 목록 API 는
    // 파트너에게도 열려 있다 - 발급 모달이 계약을 골라야 하고, 서버가 파트너를 자기
    // 계약으로 한정한다. 서버가 막는 것은 계약을 만들고 고치는 쪽이다.
    { path: "/validation",       titleKey: "nav.validation" },
    { path: "/downloads",        titleKey: "nav.downloadHistory" },
    { path: "/auth-logs",        titleKey: "nav.authLog" },
].map((item) => ({ ...item, adminOnly: adminOnlyPaths.has(item.path) }));

export default main;
